import React from "react";
import { observer } from "mobx-react";
import { useSourceLinkContext } from "../contexts/SourceLinkContext";
import {compiler} from "markdown-to-jsx";
import {interpolate, InterpolatedProperty, interpolateProps, IPROP_PREFIX} from "./interpolation";
import {deriveTemplate} from "./index";
import {SafeRenderer} from "../components/SafeRenderer";
import SourceIcon from "../components/SourceIcon";
import OutboundLink from "../components/OutboundLink";
import {escapeMD, prepareMarkdown} from "./utils";
import * as Analytics from "../lib/Analytics";
import { captureMessage, isPlexId } from "../lib/utils";
import {DEV} from "../lib/configMgr";
import { extractIdentifierFromUrl, isNumeric } from './urlContextExtraction';

// Renders all markdown links in the conversation pane as plain text + inline
// source icon (no clickable <a>). The actual URL is accessible via the sources panel.
//
// Defined at module scope so its reference is stable across renders — a new
// function reference each render causes React to unmount/remount every link,
// resetting sourceIds state and causing the icon to flash during streaming.
//
// Supports pipe-separated URLs with optional per-URL labels:
//   Single: [text](url)
//   Multiple: [text](url1|url2|url3)
//   With titles/tooltips: [text](url1|url2 "title1|title2")
const SourcesLink = observer(({href, children, title: mdTitle}) => {
  const [sourceIds, setSourceIds] = React.useState([]);
  const spanRef = React.useRef(null);
  const ctx = useSourceLinkContext();
  const { sourceReferencesStore, turnId, agentId, urlContextMap, datasets } = ctx || {};


  // Register source(s) with store - must happen in useEffect, not during render
  React.useEffect(() => {
    // Skip registration for unresolved Plex IDs — wait for interpolateSourceLinks
    // to convert them to real URLs. Span still renders; icon appears on re-render.
    if (!href || isPlexId(href)) {
      return;
    }

    if (sourceReferencesStore && turnId && href) {
      // Parse pipe-separated sources. Supports two formats:
      // New: url "label"|url "label"  — label is inline per source
      // Old: url1|url2 with labels in mdTitle  — backwards compat
      let urls, labels;
      if (href.includes('|')) {
        const segments = href.split('|').map(s => s.trim());
        const hasInlineLabels = segments.some(s => s.includes('"'));
        if (hasInlineLabels) {
          urls = []; labels = [];
          for (const seg of segments) {
            const m = seg.match(/^([^"]+?)(?:\s+"([^"]*)")?$/);
            urls.push(m[1].trim());
            labels.push(m[2] || undefined);
          }
        } else {
          // Old format fallback: urls in href, labels in title attribute
          urls = segments;
          labels = mdTitle?.includes('|')
            ? mdTitle.split('|').map(l => l.trim())
            : mdTitle ? [mdTitle] : [];
        }
      } else {
        urls = [href];
        labels = mdTitle ? [mdTitle] : [];
      }

      // Register each URL with its corresponding label (if available)
      const ids = urls.map((url, index) => {
        let perUrlLabel = labels[index]; // May be undefined

        // mdTitle may contain a Plex ID set by interpolateSourceLinks for tracking —
        // ignore it as a display label; let link text take priority instead.
        if (perUrlLabel && isPlexId(perUrlLabel)) {
          perUrlLabel = undefined;
        }

        // Title extraction with 4-tier priority:
        // 1. Per-URL label from title attribute (e.g., "BD1 TR-FRET") - but NOT Plex IDs
        // 2. Link text (resolved entity names after interpolateSourceLinks, e.g., "BRD2")
        // 3. Context from urlContextMap (e.g., "IC50 = 5.2 nM")
        // 4. Identifier extracted from URL (fallback)
        let title;
        if (perUrlLabel) {
          title = perUrlLabel;
        } else {
          const extractText = (node) => {
            if (typeof node === 'string') return node;
            if (node?.props?.children) return extractText(node.props.children);
            if (Array.isArray(node)) return node.map(extractText).join('');
            return '';
          };
          let childText = extractText(children) || null;
          if (childText) childText = childText.replace(/\\([[\]])/g, '$1');

          if (childText && !isNumeric(childText)) {
            title = childText;
          } else {
            const identifier = extractIdentifierFromUrl(url);
            title = (urlContextMap && urlContextMap.has(url))
              ? urlContextMap.get(url)
              : identifier || url;
          }
        }

        return sourceReferencesStore.addSource(url, title, turnId, agentId);
      }).filter(id => id !== null);

      // Only update if the resolved IDs actually changed — prevents flash during
      // streaming where href briefly changes and re-triggers the effect.
      setSourceIds(prev => {
        if (prev.length === ids.length && prev.every((id, i) => id === ids[i])) return prev;
        return ids;
      });
    }
  }, [href, mdTitle, sourceReferencesStore, turnId, agentId, urlContextMap]);
  // Only access highlightedSourceId if this instance has registered IDs — avoids
  // subscribing every SourcesLink to every hover event via MobX tracking.
  const isHighlighted = sourceIds.length > 0 &&
    sourceIds.some(id => sourceReferencesStore?.highlightedSourceIds?.includes(id));
  const isScrollTarget = sourceIds.length > 0 &&
    sourceIds.includes(sourceReferencesStore?.scrollToSourceId ?? null);

  // Scroll to the first occurrence when a card is clicked in the sources drawer,
  // but only if no highlighted instance is already visible in the viewport.
  React.useEffect(() => {
    if (isScrollTarget && spanRef.current) {
      const all = document.querySelectorAll('.link-highlighted');
      if (all.length === 0) return;
      const margin = 40;
      const anyVisible = Array.from(all).some(el => {
        const r = el.getBoundingClientRect();
        // Find the nearest scrollable ancestor to check against its visible bounds
        let scroller = el.parentElement;
        while (scroller && scroller !== document.body) {
          const { overflowY } = getComputedStyle(scroller);
          if (overflowY === 'auto' || overflowY === 'scroll') break;
          scroller = scroller.parentElement;
        }
        const bounds = scroller && scroller !== document.body
          ? scroller.getBoundingClientRect()
          : { top: 0, bottom: window.innerHeight };
        return r.top >= bounds.top + margin && r.bottom <= bounds.bottom - margin;
      });
      if (!anyVisible && all[0] === spanRef.current) {
        spanRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }
  }, [isScrollTarget]);

  const handleMouseEnter = () => {
    if (!sourceReferencesStore) return;
    if (sourceIds.length > 0) {
      sourceReferencesStore.highlightSource(sourceIds);
    }
  };

  const handleSourceIconClick = (e) => {
    if (!sourceReferencesStore) return;
    e.preventDefault();
    e.stopPropagation();
    if (!sourceReferencesStore.isDrawerOpen) {
      sourceReferencesStore.toggleDrawer();
    }
    if (sourceIds.length > 0) {
      sourceReferencesStore.scrollToSource(sourceIds[0]);
    }
  };

  // Use a short delay so the highlight isn't cleared when the cursor moves
  // from the span text onto the SourceIcon child — the icon's mouseenter
  // will cancel this timer before it fires.
  const highlightTimerRef = React.useRef(null);
  const handleMouseLeave = (e) => {
    if (!sourceReferencesStore) return;
    // If moving to a child element (the source icon), don't clear yet
    if (e.relatedTarget instanceof Node && spanRef.current?.contains(e.relatedTarget)) return;
    highlightTimerRef.current = setTimeout(() => {
      sourceReferencesStore.clearHighlight();
    }, 50);
  };

  // Cancel the clear-timer if cursor re-enters the span (e.g. from icon back to text)
  const handleMouseEnterSpan = (e) => {
    if (highlightTimerRef.current) {
      clearTimeout(highlightTimerRef.current);
      highlightTimerRef.current = null;
    }
    handleMouseEnter();
  };

  React.useEffect(() => () => {
    if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
  }, []);

  // Outside the LLM chat context (no SourceLinkContext provider), or for non-http
  // schemes (mailto:, tel:, ftp:, etc.), fall back to a standard outbound link.
  // Any https:// link in the LLM context is treated as a citation source — this covers
  // both Plex-resolved links (where interpolateSourceLinks embeds a Plex ID in the title)
  // and direct https:// URLs written by the LLM (e.g. ChEMBL assay pages).
  const handleLinkClick = React.useCallback((e) => {
    if (!mdTitle || !isPlexId(mdTitle) || !datasets) return;
    const dsKey = mdTitle.split(':')[0];
    const dataset = datasets[dsKey];
    if (dataset?.access_instructions) {
      e.preventDefault();
      const sourceId = mdTitle.includes(':') ? mdTitle.split(':').slice(1).join(':') : mdTitle;
      const instructions = dataset.access_instructions.replace(/\{id}/g, sourceId);
      sourceReferencesStore.showAccessInstructions(href, instructions, dataset.name || dsKey);
    }
  }, [href, mdTitle, datasets, sourceReferencesStore]);

  const isHttpLink = /^https?:/.test(href);
  if (!sourceReferencesStore || !turnId || !isHttpLink) {
    // Strip scheme prefix from label when it matches the href (e.g. "mailto:foo@bar.com" → "foo@bar.com")
    let displayChildren = children;
    if (href && typeof children === 'string' && children === href) {
      const colonIdx = href.indexOf(':');
      if (colonIdx !== -1) {
        displayChildren = href.slice(colonIdx + 1);
      }
    }
    return <OutboundLink href={href} title={mdTitle}>{displayChildren}</OutboundLink>;
  }

  // Render as clickable link (opens in new tab) + inline source icon.
  return (
    <span
      ref={spanRef}
      className={`link${isHighlighted ? ' link-highlighted' : ''}`}
      onMouseEnter={handleMouseEnterSpan}
      onMouseLeave={handleMouseLeave}
    >
      <a href={href} target="_blank" rel="noopener noreferrer" onClick={handleLinkClick}>{children}</a>
      <span onClick={handleSourceIconClick}><SourceIcon sourceIds={sourceIds} store={sourceReferencesStore} /></span>
    </span>
  );
});

// Default component overrides available to all markdown rendering contexts
// These components can appear in LLM-generated markdown and need to be
// available even when rendered outside of the chat context
const DEFAULT_COMPONENT_OVERRIDES = {
  "llm-training": {
    component: ({ children }) => (<span className="llm-training">{children}</span>)
  },
  "thinking": {
    component: ({ children }) => (<span className="thinking power-user">{children}</span>)
  },
};

const isReactComponent = (componentType, templateOverrides = {}) => {
  if (typeof(componentType) === "function") {
    return true;
  }
  if (componentType === null || componentType === undefined) {
    return false;
  }
  if (templateOverrides[componentType]) {
    componentType = templateOverrides[componentType];
    // Check again after override
    if (componentType === null || componentType === undefined) {
      return false;
    }
  }
  return typeof(componentType) !== 'string' && typeof(componentType.type) !== 'string';
}

const safeCompile = (markdown, options) => {
  try {
    return compiler(markdown, options);
  }
  catch(error) {
    console.error(`Markdown compile error: ${error}`, JSON.stringify({markdown, options}));

    // Capture markdown compilation errors to Sentry with markdown context only
    Promise.resolve(captureMessage(`Markdown compilation error: ${error?.message || String(error)}`, {
      level: "error",
      asException: true,
      errorName: "MarkdownCompilationError",
      fingerprint: ["markdown-compilation-error", error?.message || "unknown"],
      tags: {
        error_type: "markdown_compilation",
      },
      context: {
        compilation_error: {
          error_message: error?.message || String(error),
          markdown_length: markdown?.length || 0,
          markdown_preview: markdown?.substring(0, 500) || "",
        },
      },
    })).catch(sentryError => {
      console.error("Failed to capture markdown compilation error to Sentry", sentryError);
    });

    return (<span className="error">UI generation error</span>);
  }
}


export const createMarkdownElement = ({
                                        template,
                                        templateType = "no-template-type",
                                        overrides = {},
                                        entity = {},
                                        markText = null,
                                        errorMessage = null,
                                        prepare = true,
                                        trapErrors = true,
                                        disableLinks = false,
                                        optimizeForStreaming = false,
                                      }) => {

  // Designate properties passed to _every_ component within this element; this object gets augmented with interpolated
  // and formatted properties.  It also injects an "entity" prop into every component.
  const componentProps = {
    ...(overrides.props || {}),
    entity,
    markText,
  };
  const {forceDivParagraphs = true} = componentProps;

  // Maintain a list of all interpolation values to enable uniqueness checks
  const uniqueValues = new Set();
  // Use a custom createElement when compiling markdown so that we can inject
  // additional properties (entity and text marking) into React components generated from the markdown
  // See also https://github.com/quantizor/markdown-to-jsx/issues/479 w/r/t table => thead/tbody handling
  const createElement = (type, props, ...children) => {
    // You'd think that the 'forceBlock' param to the compiler would do this, but alas no...
    // Don't use "p" for paragraphs to avoid warnings on nested markdown
    // "div" not allowed within "p"

    // Ensure props is never null/undefined
    if (!props) {
      props = {};
    }

    const tag = typeof(type) === "function" ? type.name : type;
    // Remap unknown HTML tags (e.g. LLM-generated <totals>, <sources>) to span to suppress React warnings
    if (typeof type === "string" && type === type.toLowerCase() && !type.includes('-')) {
      const testEl = document.createElement(type);
      if (testEl.constructor === HTMLUnknownElement) {
        props.className = [`tag-${type}`, props.className].filter(Boolean).join(' ');
        type = "span";
      }
    }
    if (type === "p" && forceDivParagraphs) {
      type = "div";
      if (!props.className) {
        props.className = "md-p"
      }
      else {
        props.className = `${props.className} md-p`
      }
    }
    children.forEach((child, index, array) => {
      if (child && typeof(child) !== 'string') {
        if (child.forEach) {
          child.forEach((subChild, subIndex, subArray) => {
            if (subChild && typeof(subChild) !== 'string') {
              const resolvedProps = interpolateChildProps(subChild.type, subChild.props, entity,
                                                          uniqueValues, componentProps || {}, markText, overrides);
              subArray[subIndex] = React.cloneElement(subChild, resolvedProps, subChild.props?.children);
            }
          });
        }
        else {
          const resolvedProps = interpolateChildProps(child.type, child.props, entity,
                                                      uniqueValues, componentProps || {}, markText, overrides);
          array[index] = React.cloneElement(child, resolvedProps, child.props?.children);
        }
      }
    });
    // Interpolated component-specific props override interpolated props
    // Ensure all objects are defined to prevent "Cannot read properties of undefined" errors
    const overrideProps = {
      ...(overrides && overrides[tag] && overrides[tag].props ? overrides[tag].props : {}),
      ...(componentProps || {})
    };
    const resolvedProps = interpolateChildProps(type, props, entity,
                                                         uniqueValues, overrideProps, markText, overrides);
    return React.createElement(type, resolvedProps, ...children);
  };
  // The markdown compiler will inject override properties into components and ignore anything interpolated
  // in the markup.  Remove such override properties since interpolation has already set up placeholders and backing properties
  // Avoid modifying the original property settings.
  const updatedOverrides = Object.fromEntries(Object.entries(overrides).map(([k, v]) => {
    // Skip 'props' - it's not a component override, it's used for component props at line 74
    if (k === 'props') {
      return [k, v];  // Keep as-is, don't process as a component override
    }
    // If v is a function/component (not an object with .component property), keep it as-is
    // Spreading a function loses the reference: {...fn} => {props: {}} with no .component property
    if (typeof v === 'function') {
      return [k, v];
    }
    // Skip undefined or null values to prevent overriding valid defaults
    if (v === undefined || v === null) {
      console.warn(`[Markdown] Skipping override for '${k}' with ${v === null ? 'null' : 'undefined'} value`, {
        templateType,
        entity_category: entity?.category,
        stack: new Error().stack
      });
      // Capture to Sentry to track where these are coming from
      captureMessage(`Invalid markdown override: ${k} is ${v === null ? 'null' : 'undefined'}`, {
        level: "warning",
        tags: {
          error_type: "invalid_markdown_override",
          template_type: templateType,
          override_key: k,
        },
        context: {
          invalid_override: {
            override_key: k,
            override_value: String(v),
            template_type: templateType,
            entity_category: entity?.category || "N/A",
            template_preview: template?.substring(0, 200) || "",
          },
        },
      });
      return null;
    }
    // If v is an object with undefined/null component, check if it's a valid partial override
    if (v?.component === undefined || v?.component === null) {
      // Allow partial overrides that only specify props (will be completed during interpolation)
      // Example: {Entity: {props: {datasets, disableSourceLink: true}}}
      if (v?.props && Object.keys(v).length === 1) {
        // Valid partial override - only has props, component will be added by interpolation
        return [k, {...v, props: {...v.props}}];
      }
      // Invalid: has other keys or no props at all
      console.warn(`[Markdown] Skipping override for '${k}' with undefined/null component property`, {
        override: v,
        templateType,
        entity_category: entity?.category,
        stack: new Error().stack
      });
      // Capture to Sentry to track where these are coming from
      captureMessage(`Invalid markdown override: ${k}.component is ${v?.component === null ? 'null' : 'undefined'}`, {
        level: "warning",
        tags: {
          error_type: "invalid_markdown_override_component",
          template_type: templateType,
          override_key: k,
        },
        context: {
          invalid_override: {
            override_key: k,
            override_structure: JSON.stringify(Object.keys(v || {})),
            has_props: !!v?.props,
            template_type: templateType,
            entity_category: entity?.category || "N/A",
            template_preview: template?.substring(0, 200) || "",
          },
        },
      });
      return null;
    }
    return [k, {...v, props: {...v.props}}];
  }).filter(entry => entry !== null));
  let interpolated = interpolate(template,
                                   entity,
                                   componentProps,
                                   markText,
                                   uniqueValues,
                                   updatedOverrides,
                                   escapeMD);

  // Strip markdown links when disableLinks is true to prevent unclickable links in autosuggest
  if (disableLinks) {
    // Remove markdown link syntax: [text](url) -> text
    // This handles links from backend data (e.g., uniprot accessions in descriptions)
    interpolated = interpolated.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
  }

  // FIXME avoid applying linkOverride if already using an outbound link

  // Disable links in autosuggest dropdown since they can't be clicked
  const linkOverride = disableLinks
    ? {a: ({href, children, ...props}) => <span {...props}>{children}</span>}
    : {a: {component: SourcesLink}};
  const md = markText && interpolated === template
             ? `<div class="interpolated">${markText(interpolated, {asString: true})}</div>`
             : interpolated;

  // Final validation: Remove any overrides with undefined/null components that slipped through
  // This is a defensive measure to prevent React errors when rendering
  const finalOverrides = {...updatedOverrides};
  Object.entries(finalOverrides).forEach(([key, value]) => {
    // Skip 'props' key (already handled earlier)
    if (key === 'props') {
      return;
    }

    // Check if the value is an object with a component property
    if (value && typeof value === 'object' && 'component' in value) {
      if (value.component === undefined || value.component === null) {
        console.error(`[Markdown] CRITICAL: Removing override '${key}' with ${value.component === null ? 'null' : 'undefined'} component that passed validation`, {
          override_key: key,
          override_value: value,
          template_type: templateType,
          entity_category: entity?.category,
          stack: new Error().stack
        });

        // For LLM markdown, attach the full template as a file to help debug
        const attachments = [];
        if (templateType === "explicit-template" && template?.length > 1000) {
          attachments.push({
            filename: "markdown-template.md",
            data: template,
            contentType: "text/markdown",
          });
        }

        captureMessage(`Critical: Invalid override '${key}' with undefined/null component passed validation`, {
          level: "error",
          tags: {
            error_type: "critical_invalid_markdown_override",
            template_type: templateType,
            override_key: key,
          },
          context: {
            critical_override: {
              override_key: key,
              override_structure: JSON.stringify(Object.keys(value || {})),
              has_component: 'component' in value,
              component_type: typeof value.component,
              component_value: String(value.component),
              template_type: templateType,
              entity_category: entity?.category || "N/A",
              markdown_attached: attachments.length > 0,
            },
          },
          attachments,
        });
        delete finalOverrides[key];
      }
    }
    // Also check if value itself is undefined/null (direct component reference)
    else if (value === undefined || value === null) {
      console.error(`[Markdown] CRITICAL: Removing override '${key}' with ${value === null ? 'null' : 'undefined'} direct value that passed validation`, {
        override_key: key,
        template_type: templateType,
        entity_category: entity?.category,
        stack: new Error().stack
      });
      captureMessage(`Critical: Invalid override '${key}' with undefined/null direct value passed validation`, {
        level: "error",
        tags: {
          error_type: "critical_invalid_markdown_override_direct",
          template_type: templateType,
          override_key: key,
        },
      });
      delete finalOverrides[key];
    }
  });

  // Use optimizeForStreaming to handle partial/incomplete markdown gracefully
  // This prevents undefined element types from unclosed tags during streaming
  // See: https://www.npmjs.com/package/markdown-to-jsx#streaming-markdown
  const options = {
    overrides: {InterpolatedProperty, ...linkOverride, ...finalOverrides},
    createElement,
    disableParsingRawHTML: false,  // Enable HTML parsing for welcome onboarding and other HTML content
    ...(optimizeForStreaming && { optimizeForStreaming: true })
  };
  const _compileStart = Date.now();
  const compiled = safeCompile(prepare ? prepareMarkdown(md) : md, options);
  const _compileMs = Date.now() - _compileStart;
  if (_compileMs > 50) {
    Analytics.event('chat-markdown-compile-perf', {
      duration_ms: _compileMs,
      markdown_length: md?.length || 0,
    });
  }
  if (compiled === null) {
    // Nothing to render
    return null;
  }

  // Post-compilation validation: Check if compiled result has invalid type
  // markdown-to-jsx can produce elements with undefined types for malformed markdown
  const REACT_ELEMENT_TYPE = Symbol.for('react.element');
  if (compiled && typeof compiled === 'object') {
    if (compiled.$$typeof === REACT_ELEMENT_TYPE &&
        (compiled.type === undefined || compiled.type === null)) {
      if (DEV) {
        console.error('[Markdown] Compiled result has invalid type!', {
          type: compiled.type,
          markdownPreview: md?.substring(0, 200)
        });
      }
      // Return fallback instead of trying to render invalid element
      return <div className="markdown markdown-compile-error">Unable to render content</div>;
    }
  }
  // Make all links open in a new tab
  const classNames = ["markdown", templateType];
  if (compiled && compiled.props && compiled.props.className) {
    classNames.push(compiled.props.className);
  }
  const className = classNames.join(" ");

  // Aggressively filter out any undefined/null elements from the compiled tree
  // markdown-to-jsx can produce elements with undefined types when markdown is incomplete
  // We must recursively filter these out before React's reconciliation sees them
  const filterInvalidElements = (element) => {
    // Handle null/undefined
    if (element === null || element === undefined) {
      return null;
    }

    // Handle primitives (strings, numbers, booleans) - these are always valid
    if (typeof element === 'string' || typeof element === 'number' || typeof element === 'boolean') {
      return element;
    }

    // Handle arrays - filter each element
    if (Array.isArray(element)) {
      const filtered = element
        .map(child => filterInvalidElements(child))
        .filter(child => child !== null && child !== undefined);
      return filtered.length > 0 ? filtered : null;
    }

    // Check if this is a React element
    if (typeof element === 'object' && element.$$typeof === REACT_ELEMENT_TYPE) {
      // CRITICAL: Filter out elements with invalid types
      // This is the source of "Element type is invalid" errors
      if (element.type === undefined || element.type === null) {
        if (DEV) {
          console.warn('[Markdown] Filtered element with invalid type');
        }
        // Try to extract text content from props if available
        if (element.props?.children) {
          const text = extractText(element.props.children);
          return text || null;
        }
        return null;
      }

      // Element type is valid - recursively filter children
      if (element.props?.children !== undefined) {
        const originalChildren = element.props.children;
        const isArray = Array.isArray(originalChildren);
        const childArray = isArray ? originalChildren : [originalChildren];
        const filteredChildren = childArray
          .map(child => filterInvalidElements(child))
          .filter(child => child !== null && child !== undefined);

        // If children were filtered, clone with new children
        const childrenChanged = filteredChildren.length !== childArray.length ||
                               filteredChildren.some((child, i) => child !== childArray[i]);

        if (childrenChanged) {
          try {
            // Preserve array structure if original was array, otherwise unwrap single child
            const newChildren = isArray ? filteredChildren :
                               (filteredChildren.length === 1 ? filteredChildren[0] :
                                filteredChildren.length === 0 ? undefined : filteredChildren);

            const cloned = React.cloneElement(element, { ...element.props }, newChildren);

            // Verify the cloned element still has a valid type
            if (cloned.type === undefined || cloned.type === null) {
              if (DEV) {
                console.warn('[Markdown] Cloned element has invalid type, extracting text content');
              }
              // Extract text instead of returning invalid element
              const text = extractText(cloned);
              return text || null;
            }

            return cloned;
          } catch (error) {
            if (DEV) {
              console.warn('[Markdown] Clone failed during filtering, returning original:', error.message);
            }
            // If clone fails, return original - better than nothing
            return element;
          }
        }
      }

      return element;
    }

    // Not a recognized type - filter it out
    if (DEV) {
      console.warn('[Markdown] Filtered unrecognized type:', typeof element);
    }
    return null;
  };

  // Helper to extract plain text from element tree (for fallback)
  const extractText = (node) => {
    if (node === null || node === undefined) return '';
    if (typeof node === 'string' || typeof node === 'number') return String(node);
    if (Array.isArray(node)) return node.map(extractText).join('');
    if (typeof node === 'object' && node.props?.children) {
      return extractText(node.props.children);
    }
    return '';
  };

  let filtered;
  try {
    filtered = filterInvalidElements(compiled);
  } catch (error) {
    console.error('[Markdown] Filter failed:', error, 'Template:', template?.substring(0, 100));
    // If filtering fails entirely, return the original and let SafeRenderer handle it
    filtered = compiled;
  }

  // If filtering removed everything, return empty div (better than null for SafeRenderer)
  if (!filtered) {
    if (DEV) {
      console.warn('[Markdown] All elements filtered out, template:', template?.substring(0, 100));
    }
    return <div className="markdown-filtered"></div>;
  }

  // Handle primitives (string, number, boolean) that are valid React children
  // filterInvalidElements returns primitives when markdown compiles to simple text
  // or when extractText is called on invalid elements
  if (typeof filtered === 'string' || typeof filtered === 'number' || typeof filtered === 'boolean') {
    return <div className={className}>{filtered}</div>;
  }

  // Handle arrays of React children (can contain elements and/or primitives)
  if (Array.isArray(filtered)) {
    return <div className={className}>{filtered}</div>;
  }

  // Pre-clone safety check: verify filtered element is a valid React element with valid type
  const isValidElement = filtered &&
                        typeof filtered === 'object' &&
                        filtered.$$typeof === REACT_ELEMENT_TYPE &&
                        filtered.type !== undefined &&
                        filtered.type !== null;

  if (!isValidElement) {
    if (DEV) {
      console.error('[Markdown] Filtered result is not a valid React element!', {
        hasFiltered: !!filtered,
        typeofFiltered: typeof filtered,
        isReactElement: filtered?.$$typeof === REACT_ELEMENT_TYPE,
        hasValidType: filtered?.type !== undefined && filtered?.type !== null,
        actualType: filtered?.type
      });
    }
    // Return text content as fallback
    const text = extractText(filtered);
    return <div className={className}>{text || "Content unavailable"}</div>;
  }

  let element;
  try {
    element = React.cloneElement(filtered, {...filtered.props, ...(isReactComponent(filtered.type) ? componentProps : {}), className});
  } catch (error) {
    if (DEV) {
      console.warn('[Markdown] Failed to clone filtered element:', error.message);
    }
    // If cloneElement fails, wrap in a div
    return <div className={className}>{filtered}</div>;
  }

  // Final safety check: verify element has valid type after cloning
  if (!element || element.type === undefined || element.type === null) {
    if (DEV) {
      console.error('[Markdown] Element has invalid type after cloning!', {
        hasElement: !!element,
        elementType: element?.type,
        templateType,
        markdownPreview: template?.substring(0, 200)
      });
    }

    // Capture to Sentry with template context for debugging Gemini issues
    captureMessage('Invalid element type after markdown cloning', {
      level: "warning",
      tags: {
        error_type: "invalid_element_after_clone",
        template_type: templateType,
      },
      context: {
        invalid_element: {
          has_element: !!element,
          element_type: String(element?.type),
          template_type: templateType,
          markdown_length: template?.length || 0,
          markdown_preview: template?.substring(0, 500) || "",
        },
      },
    });

    // Return text content as fallback
    const text = extractText(filtered);
    return <div className={className}>{text || "Content unavailable"}</div>;
  }

  const handleRenderError = (error, info) => {
    // Don't use console.error here - it triggers Sentry's console integration
    // and creates duplicate events (WEBAPP-DD) in addition to our explicit captureMessage (WEBAPP-G4)
    // Only log to console in development for debugging
    if (DEV) {
      console.error(`[Markdown] Render error "${error}"`, {...entity, ...componentProps}, template);
    }

    // Build comprehensive context for Sentry including debug info
    const overrideKeys = Object.keys(finalOverrides || updatedOverrides);
    const debugContext = {
      markdown_render_error: {
        error_message: error?.message || String(error),
        component_stack: info?.componentStack || "N/A",
        markdown_length: template?.length || 0,
        markdown_preview: template?.substring(0, 500) || "",
        element_type: element?.type || "unknown",
        entity_category: entity?.category || "N/A",
        override_keys: overrideKeys.join(", "),
      },
    };

    // For LLM markdown errors, attach the full markdown as a file for complete analysis
    // Don't include full markdown in context - it can exceed Sentry size limits
    const attachments = [];
    if (templateType === "explicit-template" && template?.length > 1000) {
      attachments.push({
        filename: "markdown-template.md",
        data: template,
        contentType: "text/markdown",
      });

      // Include component props and override details as separate attachments for debugging
      debugContext.markdown_render_error.component_props_attached = true;
      attachments.push({
        filename: "component-props.json",
        data: JSON.stringify(componentProps, null, 2),
        contentType: "application/json",
      });

      attachments.push({
        filename: "overrides.json",
        data: JSON.stringify(
          Object.fromEntries(
            overrideKeys.map(key => [
              key,
              {
                type: typeof (finalOverrides || updatedOverrides)[key],
                value: String((finalOverrides || updatedOverrides)[key])
              }
            ])
          ),
          null,
          2
        ),
        contentType: "application/json",
      });
    }

    // Capture markdown rendering errors to Sentry with full context
    Promise.resolve(captureMessage(`Markdown render error: ${error?.message || String(error)}`, {
      level: "error",
      asException: true,
      errorName: "MarkdownRenderError",
      fingerprint: ["markdown-render-error", templateType, error?.message || "unknown"],
      tags: {
        error_type: "markdown_render",
        template_type: templateType,
      },
      context: debugContext,
      attachments,
    })).catch(sentryError => {
      console.error("Failed to capture markdown render error to Sentry", sentryError);
    });
  };

  // Don't use resetKeys at all - let the error boundary persist across renders
  // SafeRenderer will automatically catch and display errors without needing to reset
  // Resetting causes unmount/remount which is the source of the "jumpy" rendering
  return (
    <SafeRenderer
      className="markdown-safe-renderer"
      errorMessage={errorMessage || `Markdown render error`}
      onError={handleRenderError}
    >
      {element}
    </SafeRenderer>
  );
};

const interpolateChildProps = (type, props, entity, uniqueValues, componentProps, markText = null, templateOverrides = {}) => {
  if (!props) {
    props = {};
  }
  Object.entries(props).forEach(([key, value]) => {
    // Replace property placeholders
    if (typeof(value) === "string" && value.startsWith(IPROP_PREFIX)) {
      props[key] = componentProps[value];
    }
  });
  const isDOMElement = !isReactComponent(type, templateOverrides);
  const interpolated = interpolateProps(props, entity, componentProps, uniqueValues, markText, templateOverrides);
  return Object.entries({...props, ...interpolated}).reduce((result, [key, value]) => {
    if (!key.startsWith(IPROP_PREFIX)) {
      result[key] = value;
    }
    return result;
  }, isDOMElement ? {} : {entity, markText});
};

export const renderMarkdownWithInterpolation = ({
                                                  templateType,
                                                  templates,
                                                  templateOverrides = {},
                                                  entity,
                                                  fallback = "",
                                                  markText = null,
                                                  disableLinks = false,
                                                }) => {
  const template = deriveTemplate(templates, templateType, entity.category, entity.baseCategory, null, fallback);
  return createMarkdownElement({
                                 template: template,
                                 templateType,
                                 overrides: templateOverrides,
                                 entity,
                                 markText,
                                 disableLinks,
                               });
};

export const renderMarkdown = ({markdown = "", entity = {}, markText = null, overrides = {},
                                 errorMessage = null, prepare = true, trapErrors = true, optimizeForStreaming = false}) => {
  try {
    // Extract props separately - it's not a component override, it's component props
    const {props: componentProps, ...componentOverrides} = overrides;
    return createMarkdownElement({
                                   template: markdown,
                                   templateType: "explicit-template",
                                   overrides: {
                                     ...DEFAULT_COMPONENT_OVERRIDES,  // Apply defaults first
                                     ...componentOverrides,            // Allow caller overrides to replace defaults
                                     props: {...(componentProps || {})}
                                   },
                                   entity, markText, errorMessage, prepare, optimizeForStreaming
                                 });
  } catch (error) {
    if (trapErrors) {
      console.error(error);

      // Capture markdown rendering setup errors to Sentry with markdown context only
      Promise.resolve(captureMessage(`Markdown rendering setup error: ${error?.message || String(error)}`, {
        level: "error",
        asException: true,
        errorName: "MarkdownRenderingSetupError",
        fingerprint: ["markdown-rendering-setup-error", error?.message || "unknown"],
        tags: {
          error_type: "markdown_rendering_setup",
        },
        context: {
          rendering_error: {
            error_message: error?.message || String(error),
            markdown_length: markdown?.length || 0,
            markdown_preview: markdown?.substring(0, 500) || "",
            has_entity: !!entity,
            entity_category: entity?.category || "N/A",
          },
        },
      })).catch(sentryError => {
        console.error("Failed to capture markdown rendering error to Sentry", sentryError);
      });

      return <span className="error-message error">{error.message}</span>;
    }
    throw error;
  }
};
